import React, { useState } from "react";
import {
  Gamepad2,
  Lightbulb,
  Bot,
  Building2,
  Cpu,
  Clock,
  ArrowRight,
} from "lucide-react";
import { speedComparisons } from "../data/mock";

const categoryIcons = {
  "game-controller": Gamepad2,
  "led-light-bar": Lightbulb,
  "robotic-finger": Bot,
  "tower-cap": Building2,
  connector: Cpu,
};

export const SlaVsFdmSection = () => {
  const [activeId, setActiveId] = useState("game-controller");
  const active = speedComparisons.find((c) => c.id === activeId);

  if (!active) return null;

  const maxMinutes = Math.max(
    ...Object.values(active.times).map((t) => t.minutes)
  );

  const timeEntries = Object.entries(active.times);

  return (
    <section className="py-24 lg:py-32 bg-[#080808] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.02] rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-14 lg:mb-16">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            SLA vs FDM
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Get more done with most prints{" "}
            <span className="text-[#F76B1C]">in under 2 hours.</span>
          </h2>
          <p className="mt-6 text-lg text-white/45 leading-relaxed">
            Form 4 outperforms both FDM and traditional SLA printers across
            real-world use cases. See how it compares on actual production
            parts.
          </p>
        </div>

        {/* Main content: speed card + image */}
        <div className="grid lg:grid-cols-[400px_1fr] xl:grid-cols-[440px_1fr] gap-8 lg:gap-12 mb-12">
          {/* Speed comparison card */}
          <div className="rounded-2xl bg-[#111111] border border-white/[0.06] overflow-hidden">
            {/* Form 4 highlight */}
            <div className="p-6 bg-[#F76B1C] relative">
              <div className="flex items-center justify-between mb-1">
                <span className="text-4xl lg:text-5xl font-extrabold text-white">
                  {active.times.form4.display}
                </span>
                <span className="text-white/70 text-sm font-bold uppercase tracking-wider">
                  Form 4
                </span>
              </div>
              <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${(active.times.form4.minutes / maxMinutes) * 100}%`,
                  }}
                />
              </div>
            </div>

            {/* Competitor bars */}
            <div className="p-6 space-y-5">
              {timeEntries.slice(1).map(([key, time]) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 text-lg font-bold">
                      {time.display}
                    </span>
                    <span className="text-white/30 text-xs font-semibold uppercase tracking-wider">
                      {time.label}
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white/20 rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${(time.minutes / maxMinutes) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Product label */}
            <div className="px-6 pb-6 pt-2 border-t border-white/[0.06]">
              <p className="text-white/40 text-sm">
                <span className="text-white/60 font-medium">
                  {active.company}
                </span>
                , {active.product}
              </p>
            </div>
          </div>

          {/* Product image */}
          <div className="relative rounded-2xl overflow-hidden bg-[#111111] border border-white/[0.06] flex items-center justify-center min-h-[360px]">
            <img
              src={active.image}
              alt={active.product}
              className="w-full h-full object-cover absolute inset-0 transition-opacity duration-500"
              key={active.id}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 via-transparent to-transparent" />

            {/* Speed advantage badge */}
            <div className="absolute top-5 right-5 px-4 py-2 bg-[#F76B1C]/90 backdrop-blur-md rounded-xl">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-white" />
                <span className="text-white text-sm font-bold">
                  {Math.round(
                    ((Math.min(
                      active.times.fdm.minutes,
                      active.times.sla.minutes
                    ) -
                      active.times.form4.minutes) /
                      Math.min(
                        active.times.fdm.minutes,
                        active.times.sla.minutes
                      )) *
                      100
                  )}
                  % faster than closest competitor
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {speedComparisons.map((item) => {
            const IconComp = categoryIcons[item.id];
            const isActive = activeId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveId(item.id)}
                className={`group relative p-4 rounded-xl text-left transition-all duration-300 ${
                  isActive
                    ? "bg-[#111111] border-[#F76B1C]/30 border"
                    : "bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.1] hover:bg-white/[0.04]"
                }`}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#F76B1C] rounded-full" />
                )}

                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${
                      isActive
                        ? "bg-[#F76B1C]/15"
                        : "bg-white/[0.04] group-hover:bg-white/[0.06]"
                    }`}
                  >
                    {IconComp && (
                      <IconComp
                        size={16}
                        className={
                          isActive ? "text-[#F76B1C]" : "text-white/40"
                        }
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold truncate ${
                        isActive ? "text-white" : "text-white/60"
                      }`}
                    >
                      {item.product}
                    </p>
                    <p className="text-[11px] text-white/30 font-medium">
                      {item.industry}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom note + CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-white/25 text-xs max-w-xl">
            Typical print time using 80th percentile print height (53 mm), 100
            µm layer height. Print time is similar whether you&apos;re printing
            one part or a full build of many parts.
          </p>
          <a
            href="#printers"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#printers");
              if (el)
                window.scrollTo({
                  top: el.getBoundingClientRect().top + window.scrollY - 80,
                  behavior: "smooth",
                });
            }}
            className="inline-flex items-center gap-2 text-[#F76B1C] hover:text-[#FF8C42] font-semibold text-sm transition-colors duration-200 group shrink-0"
          >
            See Our Printers
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </a>
        </div>
      </div>
    </section>
  );
};
